/**
 * Network Partner admin hooks — Hub is the source of truth.
 *
 * - `useNetworkPartners(filters)` — server-paginated roster for the
 *   admin management area.
 * - `useNetworkPartner(id)` — detail + save/archive/restore/ban for the
 *   edit page (id `null` = create mode, nothing fetched).
 * - `useNetworkPartnerSearch(query)` — debounced active+prospect
 *   search backing the triage shortlist.
 * - `useNpNotes(id)` — the partner's notes stack (+ "Add note").
 */

import { useCallback, useEffect, useState } from 'react';

import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import type {
  BackofficePartnerStatus,
  NetworkPartnerDetail,
  NetworkPartnerListFilters,
  NetworkPartnerListItem,
  NetworkPartnerPayload,
  NpEngagements,
  NpNote,
  NpNotesQuery,
  NpNotesResponse,
  SubjurisdictionInfo,
} from '@/types/network-partner';

const SEARCH_DEBOUNCE_MS = 250;

type Loaded<T> = { ok: true; data: T } | { ok: false; error: Error };

/**
 * The read half shared by every partner-scoped hook below: a resource
 * keyed by partner id that loads on mount and whenever the id or the
 * loader changes, exposes `refetch`, and reports failures to Sentry.
 * `clearOnError` decides whether a failed refetch blanks the last good
 * value (list-like reads) or keeps it (the profile itself).
 */
function usePartnerResource<T>(
  partnerId: string | null,
  load: (partnerId: string) => Promise<T>,
  { fallbackMessage, clearOnError = true }: { fallbackMessage: string; clearOnError?: boolean }
) {
  const [state, setState] = useState<{ data: T | null; isLoading: boolean; error: Error | null }>({
    data: null,
    isLoading: false,
    error: null,
  });

  const refetch = useCallback(async () => {
    if (!partnerId) {
      setState({ data: null, isLoading: false, error: null });
      return;
    }
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    const result = await load(partnerId).then(
      (data): Loaded<T> => ({ ok: true, data }),
      (err: unknown): Loaded<T> => {
        const error = err instanceof Error ? err : new Error(fallbackMessage);
        Sentry.captureException(error);
        return { ok: false, error };
      }
    );
    setState((prev) =>
      result.ok
        ? { data: result.data, isLoading: false, error: null }
        : { data: clearOnError ? null : prev.data, isLoading: false, error: result.error }
    );
  }, [partnerId, load, fallbackMessage, clearOnError]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const setData = useCallback((data: T | null) => setState((prev) => ({ ...prev, data })), []);

  return { ...state, setData, refetch };
}

export function useNetworkPartners(filters: NetworkPartnerListFilters = {}) {
  const [items, setItems] = useState<NetworkPartnerListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Primitive deps so a fresh filters object doesn't refetch needlessly.
  const statusesKey = filters.statuses?.join(',') ?? '';
  const { scope, jurisdiction, language, search, needsAttention, page, pageSize } = filters;

  const fetchOnce = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await vnextApiClient.listNetworkPartners({
        statuses: statusesKey ? statusesKey.split(',') : undefined,
        scope,
        jurisdiction,
        language,
        search,
        needsAttention,
        page,
        pageSize,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (err) {
      const e = err instanceof Error ? err : new Error('Failed to load network partners');
      Sentry.captureException(e);
      setError(e);
    } finally {
      setIsLoading(false);
    }
  }, [statusesKey, scope, jurisdiction, language, search, needsAttention, page, pageSize]);

  useEffect(() => {
    void fetchOnce();
  }, [fetchOnce]);

  return { items, total, isLoading, error, refetch: fetchOnce };
}

const loadPartner = (partnerId: string) => vnextApiClient.getNetworkPartner(partnerId);

export function useNetworkPartner(partnerId: string | null) {
  // A failed refresh keeps the last good profile on screen.
  const {
    data: partner,
    setData: setPartner,
    isLoading,
    error,
    refetch,
  } = usePartnerResource<NetworkPartnerDetail>(partnerId, loadPartner, {
    fallbackMessage: 'Failed to load network partner',
    clearOnError: false,
  });

  const run = useCallback(
    async (
      fn: () => Promise<NetworkPartnerDetail>,
      failTitle: string
    ): Promise<NetworkPartnerDetail | null> => {
      try {
        const result = await fn();
        setPartner(result);
        return result;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        Sentry.captureException(e);
        notifications.show({ title: failTitle, message: e.message, color: 'red' });
        return null;
      }
    },
    []
  );

  const create = useCallback(
    (payload: NetworkPartnerPayload) =>
      run(() => vnextApiClient.createNetworkPartner(payload), 'Could not create partner'),
    [run]
  );

  const update = useCallback(
    (patch: NetworkPartnerPayload) =>
      partnerId
        ? run(() => vnextApiClient.updateNetworkPartner(partnerId, patch), 'Could not save partner')
        : Promise.resolve(null),
    [partnerId, run]
  );

  const archive = useCallback(
    () =>
      partnerId
        ? run(() => vnextApiClient.archiveNetworkPartner(partnerId), 'Could not archive partner')
        : Promise.resolve(null),
    [partnerId, run]
  );

  const restore = useCallback(
    () =>
      partnerId
        ? run(() => vnextApiClient.restoreNetworkPartner(partnerId), 'Could not restore partner')
        : Promise.resolve(null),
    [partnerId, run]
  );

  /** Irreversible: the API has no unban. */
  const ban = useCallback(
    (reason: string) =>
      partnerId
        ? run(
            () => vnextApiClient.banNetworkPartner(partnerId, { reason }),
            'Could not ban partner'
          )
        : Promise.resolve(null),
    [partnerId, run]
  );

  return { partner, isLoading, error, refetch, create, update, archive, restore, ban };
}

/** Debounced NP search for the triage shortlist (active + prospect). */
export function useNetworkPartnerSearch(query: string) {
  const [items, setItems] = useState<NetworkPartnerListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const handle = window.setTimeout(async () => {
      if (cancelled) {
        return;
      }
      // Empty query → empty list (avoid pulling the whole roster at
      // every keystroke).
      if (!query.trim()) {
        setItems([]);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const results = await vnextApiClient.searchNetworkPartners({
          q: query.trim(),
          limit: 25,
        });
        if (!cancelled) {
          setItems(results);
        }
      } catch (err) {
        if (!cancelled) {
          const e = err instanceof Error ? err : new Error('Search failed');
          setError(e.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [query]);

  return { items, loading, error };
}

/**
 * Health of a partner's Django Backoffice account. Lazily checked;
 * `createAccount` JIT-provisions and refreshes. `status.reachable ===
 * false` means Django couldn't be queried — warn, never block.
 */
const loadBackofficeStatus = (partnerId: string) =>
  vnextApiClient.getBackofficePartnerStatus(partnerId);

export function useBackofficePartnerStatus(partnerId: string | null) {
  const {
    data: status,
    setData: setStatus,
    isLoading,
    refetch,
  } = usePartnerResource<BackofficePartnerStatus>(partnerId, loadBackofficeStatus, {
    fallbackMessage: 'Failed to load the Backoffice account status',
  });
  const [isCreating, setIsCreating] = useState(false);

  const createAccount = useCallback(async () => {
    if (!partnerId) {
      return;
    }
    setIsCreating(true);
    try {
      setStatus(await vnextApiClient.createBackofficePartnerAccount(partnerId));
      notifications.show({
        title: 'Backoffice account ready',
        message: 'The partner received an activation invite.',
        color: 'green',
      });
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err));
      Sentry.captureException(e);
      notifications.show({
        title: 'Could not create Backoffice account',
        message: e.message,
        color: 'red',
      });
    } finally {
      setIsCreating(false);
    }
  }, [partnerId]);

  return { status, isLoading, isCreating, refetch, createAccount };
}

/** One page of a partner's engagement history + review aggregates. */
export function useNpEngagements(
  partnerId: string | null,
  { page = 1, pageSize = 50 }: { page?: number; pageSize?: number } = {}
) {
  const load = useCallback(
    (id: string) => vnextApiClient.getNetworkPartnerEngagements(id, { page, pageSize }),
    [page, pageSize]
  );
  const { data, isLoading, refetch } = usePartnerResource<NpEngagements>(partnerId, load, {
    fallbackMessage: 'Failed to load engagements',
  });

  return { data, isLoading, refetch };
}

/**
 * The partner's notes stack — stamped admin notes and per-engagement
 * reviews, newest first — plus the "Add note" write, which posts
 * immediately (independent of the profile form) and refetches.
 */
export function useNpNotes(partnerId: string | null, query: NpNotesQuery = {}) {
  // Primitive deps so a fresh query object doesn't refetch needlessly.
  const { page = 1, pageSize = 50, q, sort, dimension, minRating, maxRating } = query;
  const kindsKey = query.kinds?.join(',') ?? '';
  const load = useCallback(
    (id: string) =>
      vnextApiClient.listNetworkPartnerNotes(id, {
        page,
        pageSize,
        q,
        kinds: kindsKey ? kindsKey.split(',') : undefined,
        sort,
        dimension,
        minRating,
        maxRating,
      }),
    [page, pageSize, q, kindsKey, sort, dimension, minRating, maxRating]
  );
  const { data, isLoading, refetch } = usePartnerResource<NpNotesResponse>(partnerId, load, {
    fallbackMessage: 'Failed to load notes',
  });
  const [isAdding, setIsAdding] = useState(false);

  const addNote = useCallback(
    async (body: string): Promise<NpNote | null> => {
      if (!partnerId) {
        return null;
      }
      setIsAdding(true);
      try {
        const note = await vnextApiClient.addNetworkPartnerNote(partnerId, { body });
        notifications.show({
          title: 'Note added',
          message: 'Stamped with your name and the current time.',
          color: 'green',
        });
        await refetch();
        return note;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        Sentry.captureException(e);
        notifications.show({ title: 'Could not add note', message: e.message, color: 'red' });
        return null;
      } finally {
        setIsAdding(false);
      }
    },
    [partnerId, refetch]
  );

  return { data, isLoading, refetch, addNote, isAdding };
}

/** The seeded subjurisdictions (currently US states) for the location picker. */
export function useSubjurisdictions() {
  const [items, setItems] = useState<SubjurisdictionInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    vnextApiClient
      .listSubjurisdictions()
      .then((rows) => {
        if (!cancelled) {
          setItems(rows);
        }
      })
      .catch((err) => Sentry.captureException(err))
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { items, isLoading };
}
